import {
  BadgeCheck,
  Clock3,
  Flag,
  Loader2,
  ShieldAlert,
  type LucideIcon,
} from "lucide-react";
import { formatDate } from "@/features/credentials/lib/format-date";
import { PsvProviderStatus } from "@api/shared-runtime/psv-review";
import type { WorkspaceProvider } from "./workspace-types";

export type PsvStatus = NonNullable<WorkspaceProvider["psvStatus"]>;

export const PSV_PRESENTATION: Record<
  PsvProviderStatus,
  { icon: LucideIcon; badgeClassName: string; textClassName: string }
> = {
  [PsvProviderStatus.Verified]: {
    icon: BadgeCheck,
    badgeClassName: "border-emerald-200 bg-emerald-50 text-emerald-800",
    textClassName: "text-emerald-700",
  },
  [PsvProviderStatus.Concern]: {
    icon: Flag,
    badgeClassName: "border-red-200 bg-red-50 text-red-800",
    textClassName: "text-red-700",
  },
  [PsvProviderStatus.NeedsReview]: {
    icon: ShieldAlert,
    badgeClassName: "border-amber-200 bg-amber-50 text-amber-800",
    textClassName: "text-amber-700",
  },
  [PsvProviderStatus.InProgress]: {
    icon: Loader2,
    badgeClassName: "border-blue-200 bg-blue-50 text-blue-800",
    textClassName: "text-blue-700",
  },
  [PsvProviderStatus.NotRun]: {
    icon: Clock3,
    badgeClassName: "border-slate-200 bg-slate-50 text-slate-600",
    textClassName: "text-slate-600",
  },
};

function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

export function psvStatusLabel(psv: PsvStatus) {
  switch (psv.status) {
    case PsvProviderStatus.Verified:
      return "Verified";
    case PsvProviderStatus.Concern:
      return "Concern";
    case PsvProviderStatus.NeedsReview:
      return "Needs review";
    case PsvProviderStatus.InProgress:
      return psv.queued ? "Queued" : "Running";
    case PsvProviderStatus.NotRun:
      return "Not run";
  }
}

/** The line under the status badge: what the provider's latest run needs. */
export function psvStatusDetail(psv: PsvStatus) {
  switch (psv.status) {
    case PsvProviderStatus.Verified:
      return `All ${plural(psv.totalItems, "item")} accepted`;
    case PsvProviderStatus.Concern:
      return plural(psv.concerns, "concern");
    case PsvProviderStatus.NeedsReview:
      return `${plural(psv.openItems, "item")} ${psv.openItems === 1 ? "needs" : "need"} review`;
    case PsvProviderStatus.InProgress:
      if (psv.queued) {
        return "Waiting to start";
      }
      return `${psv.finishedChecks} of ${psv.totalChecks} checks finished`;
    case PsvProviderStatus.NotRun:
      return "Ready to verify";
  }
}

export function psvTitle(psv: PsvStatus) {
  const prefix = "Latest primary source verification";
  switch (psv.status) {
    case PsvProviderStatus.Verified:
      return psv.completedAt
        ? `${prefix}: all items verified on ${formatDate(psv.completedAt)}`
        : `${prefix}: all items verified`;
    case PsvProviderStatus.Concern:
      return `${prefix}: ${plural(psv.concerns, "concern")} found; open the report for details`;
    case PsvProviderStatus.NeedsReview:
      return `${prefix}: ${plural(psv.openItems, "item")} ${psv.openItems === 1 ? "needs" : "need"} review`;
    case PsvProviderStatus.InProgress:
      return `${prefix}: verification is in progress`;
    case PsvProviderStatus.NotRun:
      return "Primary source verification has not been run for this provider";
  }
}

/** Text color for the status, for places that show it without a badge. */
export function psvStatusTextClassName(status: PsvProviderStatus) {
  return PSV_PRESENTATION[status].textClassName;
}
